/**
 * Pure rate and display helpers for currency-rates.
 */

/**
 * Resolve the USD→base exchange rate, or null when base has no known rate.
 * @param {Record<string, number>} usdRates
 * @param {string | null} base
 * @returns {number | null}
 */
export function resolveUsdToBase(usdRates, base) {
	const rate = base === 'USD' ? 1 : (base ? usdRates[base] : undefined);
	return rate !== undefined && rate !== 0 ? rate : null;
}

/**
 * Compute the base-relative rate for a currency.
 * @param {string} code
 * @param {number | null} manualRate
 * @param {number | null} usdToBase
 * @param {Record<string, number>} usdRates
 * @returns {number | null}
 */
export function computeRate(code, manualRate, usdToBase, usdRates) {
	if (usdToBase === null) {
		return manualRate ?? null;
	}
	if (code === 'USD') {
		return usdToBase;
	}
	if (manualRate !== null && manualRate !== undefined) {
		return manualRate;
	}
	const usdToCcy = usdRates[code];
	if (usdToCcy !== undefined && usdToCcy !== 0) {
		return usdToBase / usdToCcy;
	}
	return null;
}

/**
 * Compute base-relative rate for a single currency row.
 * @param {Record<string, any>} currency
 * @param {Record<string, number>} usdRates
 * @param {string | null} baseCurrency
 * @returns {number | null}
 */
export function rateFor(currency, usdRates, baseCurrency) {
	if (currency.code === baseCurrency) {
		return 1;
	}
	const manual = currency.manualRate;
	if (manual !== null && manual !== undefined) {
		return Number(manual);
	}
	const usdToBase = resolveUsdToBase(usdRates, baseCurrency);
	return computeRate(currency.code, null, usdToBase, usdRates);
}

/**
 * Resolve base currency from explicit value or currency flags.
 * @param {Array<Record<string, any>>} currencies
 * @param {string | null | undefined} explicitBase
 * @param {string | null} fallbackBase
 * @returns {string | null}
 */
export function resolveBaseCurrency(currencies, explicitBase, fallbackBase) {
	if (explicitBase !== undefined && explicitBase !== null) {
		return explicitBase;
	}
	return currencies.find((c) => c.isBase)?.code ?? fallbackBase;
}

/**
 * Convert amount between two currencies using a currency map.
 * @param {number | null | undefined} amount
 * @param {string | null | undefined} fromCode
 * @param {string | null | undefined} toCode
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {number | null}
 */
export function convertBetweenWithMap(amount, fromCode, toCode, currencyMap) {
	if (amount === null || amount === undefined || !fromCode || !toCode) {
		return null;
	}
	if (fromCode === toCode) {
		return Number(amount);
	}
	const fromEntry = currencyMap.get(fromCode);
	const toEntry = currencyMap.get(toCode);
	if (!fromEntry || !toEntry || fromEntry.rate === null || fromEntry.rate === undefined || toEntry.rate === null || toEntry.rate === undefined || toEntry.rate === 0) {
		return null;
	}
	return Number(amount) * fromEntry.rate / toEntry.rate;
}

/**
 * Convert amount into base currency using a currency map.
 * @param {number | null | undefined} amount
 * @param {string | null | undefined} code
 * @param {string | null} baseCurrency
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {number | null}
 */
export function convertToBaseWithMap(amount, code, baseCurrency, currencyMap) {
	return convertBetweenWithMap(amount, code, baseCurrency, currencyMap);
}

/**
 * Resolve display metadata for a currency from a map.
 * @param {string | null | undefined} code
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {{ symbol: string | undefined, decimalPlaces: number | undefined }}
 */
export function metaForWithMap(code, currencyMap) {
	const entry = code ? currencyMap.get(code) : undefined;
	return {
		symbol: entry?.symbol,
		decimalPlaces: entry?.decimalPlaces,
	};
}

/**
 * Fill in currency display metadata on rows from a currency map.
 * @param {Array<Record<string, any>>} rows
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {Array<Record<string, any>>}
 */
export function enrichCurrencyDisplayWithMap(rows, currencyMap) {
	for (const r of rows) {
		const main = metaForWithMap(r.currency, currencyMap);
		r.currencySymbol = r.currencySymbol ?? main.symbol;
		r.currencyDecimalPlaces = r.currencyDecimalPlaces ?? main.decimalPlaces;
		if (r.pairedCurrency) {
			const paired = metaForWithMap(r.pairedCurrency, currencyMap);
			r.pairedCurrencySymbol = r.pairedCurrencySymbol ?? paired.symbol;
			r.pairedCurrencyDecimalPlaces = r.pairedCurrencyDecimalPlaces ?? paired.decimalPlaces;
		}
		if (r.destCurrency) {
			const dest = metaForWithMap(r.destCurrency, currencyMap);
			r.destCurrencySymbol = r.destCurrencySymbol ?? dest.symbol;
			r.destCurrencyDecimalPlaces = r.destCurrencyDecimalPlaces ?? dest.decimalPlaces;
		}
	}
	return rows;
}

/**
 * Attach converted base equivalents to generic rows.
 * @param {Array<Record<string, any>>} rows
 * @param {(row: Record<string, any>) => { amount: number | null | undefined, code: string | null | undefined }} accessor
 * @param {string | null} baseCurrency
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {Array<Record<string, any>>}
 */
export function attachBaseEquivalentWithMap(rows, accessor, baseCurrency, currencyMap) {
	for (const r of rows) {
		const { amount, code } = accessor(r);
		if (amount !== null && amount !== undefined && code) {
			r.baseEquivalent = convertToBaseWithMap(amount, code, baseCurrency, currencyMap);
		}
	}
	return rows;
}

/**
 * Attach balance/principal equivalents to account rows.
 * @param {Array<Record<string, any>>} accounts
 * @param {string | null} baseCurrency
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {Array<Record<string, any>>}
 */
export function attachAccountEquivalentsWithMap(accounts, baseCurrency, currencyMap) {
	for (const a of accounts) {
		a.balanceBaseEquivalent = convertToBaseWithMap(/** @type {number} */ (a.balance), /** @type {string} */ (a.currency), baseCurrency, currencyMap);
		a.principalBaseEquivalent = a.principal !== null && a.principal !== undefined ? convertToBaseWithMap(a.principal, a.currency, baseCurrency, currencyMap) : null;
	}
	return accounts;
}
